import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { format } from "date-fns";

function InvestmentTransactionList({ transactions, assetClasses, currency, onDelete }) {
  const acMap = Object.fromEntries(assetClasses.map(a => [a.asset_class_id, a.name]));
  const fmt = (n) => `${currency} ${n.toFixed(2)}`;

  return (
    <Card data-testid="investment-transactions-list" className="border border-border">
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
          All Investment Transactions
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 sm:p-6 sm:pt-0">
        {transactions.length === 0 ? (
          <div className="p-6 text-center py-12 text-muted-foreground text-sm">
            No transactions yet. Start by recording your first Buy.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Class</th>
                  <th className="py-3 px-4 text-right">Qty</th>
                  <th className="py-3 px-4 text-right">Price</th>
                  <th className="py-3 px-4 text-right">Charges</th>
                  <th className="py-3 px-4 text-right">Total</th>
                  <th className="py-3 px-4 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => {
                  const charges = (t.brokerage || 0) + (t.taxes || 0) + (t.other_charges || 0);
                  return (
                    <tr
                      key={t.transaction_id}
                      data-testid={`investment-txn-${t.transaction_id}`}
                      className="border-b border-border transition-colors duration-200 hover:bg-slate-50"
                    >
                      <td className="py-3 px-4 whitespace-nowrap">
                        {format(new Date(t.date), 'MMM dd, yyyy')}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          className={
                            t.type === 'Buy'
                              ? 'bg-success/10 text-success border-success/20'
                              : 'bg-destructive/10 text-destructive border-destructive/20'
                          }
                        >
                          {t.type}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 font-semibold">{t.symbol}</td>
                      <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                        {acMap[t.asset_class_id] || t.asset_class_id}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums">{t.quantity}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(t.price)}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">
                        {fmt(charges)}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold">
                        {fmt(t.total_amount)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              data-testid={`delete-investment-${t.transaction_id}`}
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Transaction?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Deleting this transaction will recompute your holding for {t.symbol}. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(t.transaction_id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default InvestmentTransactionList;
